import { useMemo } from 'react';

import { Alert, Card, Group, Stack, Text, Title } from '@cowbell/ui';

import { useCowbellTranslations } from '../i18n/translations';
import FindMyAgent from './FindMyAgent';
import { InterestedAgencies } from './InterestedAgencies';
import { MyAgentSummary } from './MyAgentSummary';
import { useResolvedAgent } from './useResolvedAgent';
import { useSearchOrigin } from './useSearchOrigin';
import type { PinnedAgencyContact } from './AgencyPinPopover';

/**
 * The policyholder's agent, resolved from whichever source actually has one —
 * a live policy, a bound binder, the hard-linked agency, or a live quote. The
 * agency directory search is offered only to an account with no live policy
 * and no hard-linked agency.
 */
function MyAgent() {
  const { t } = useCowbellTranslations();
  const { agent, location, interested, canSearch, isLoading, isError } =
    useResolvedAgent();

  // Where the search starts: the hard-linked agency, else the browser's
  // position, else the policyholder account's own address. Disabled while the
  // search is hidden, so the browser never prompts for a location it won't use.
  const searchOrigin = useSearchOrigin(location, canSearch);

  const interestedCount = interested.length;
  const agencyTerm = t('agencies').toLowerCase();
  const agentTerm = t('agent').toLowerCase();

  // The agency directory search returns no agent, so the map popover can only
  // name one where we already hold it: the policyholder's own agent, and the
  // agencies that registered interest.
  const contactsByAgencyId = useMemo(() => {
    const contacts: Record<string, PinnedAgencyContact> = {};

    [agent.primary, agent.secondary].forEach((resolved) => {
      if (resolved?.agencyId) {
        contacts[resolved.agencyId] = {
          agentFullName: resolved.agentFullName,
          agentEmail: resolved.agentEmail,
          agentPhone: resolved.agentPhone,
        };
      }
    });

    interested.forEach((row: any) => {
      if (row?.id) {
        contacts[row.id] = {
          agentFullName: row.contactName,
          agentEmail: row.contactEmail,
          agentPhone: row.contactPhone,
        };
      }
    });

    return contacts;
  }, [agent.primary, agent.secondary, interested]);

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <Stack gap={4}>
          {/* A plain h1 with the shared class, exactly as My Services, Quotes
              and Claims do — Mantine's Title imposes its own font-weight and
              would render this heavier than every other page heading. */}
          <h1 className="table-heading" style={{ margin: 0 }}>
            {`My ${t('agent')}`}
          </h1>
          <Text size="sm" c="dimmed" maw={560}>
            {canSearch
              ? `Your ${agentTerm} of record, the ${agencyTerm} interested in your account, and a search for licensed ${agencyTerm} near you.`
              : `Your ${agentTerm} of record and the ${agencyTerm} interested in your account.`}
          </Text>
        </Stack>

        <Stack gap={0} align="flex-end">
          <Text size="xs" c="dimmed">
            {`${t('agencies')} interested`}
          </Text>
          <Text fz="xl" fw={600}>
            {interestedCount}
          </Text>
        </Stack>
      </Group>

      {isError && (
        <Alert color="red">
          {`We couldn't load your ${t(
            'agent'
          ).toLowerCase()} just now. Please try again in a few minutes.`}
        </Alert>
      )}

      {!isLoading && (
        <MyAgentSummary
          agent={agent}
          location={location}
          canSearch={canSearch}
        />
      )}

      {canSearch && (
        <FindMyAgent
          contactsByAgencyId={contactsByAgencyId}
          currentAgencyId={agent.primary.agencyId}
          currentAgencyLocation={
            // Built from the resolved agent, not the location lookup: that lookup
            // returns nothing for agencies whose location records the directory
            // cannot filter to, and the policyholder's own agency must be listed
            // either way. Any address and geo point we do have are folded in.
            agent.primary.agencyId
              ? {
                  ...(location ?? {}),
                  id: location?.id ?? `agency-${agent.primary.agencyId}`,
                  agencyId: agent.primary.agencyId,
                  name: agent.primary.agencyName ?? location?.name,
                }
              : undefined
          }
          defaultArea={searchOrigin.area}
          defaultCenter={searchOrigin.center}
        />
      )}

      <Card withBorder radius="md" padding={0}>
        <Stack gap={2} p="md">
          <Title order={2} size="h5">
            {`${t('agencies')} interested in your account`}
          </Title>
          {interestedCount > 0 && (
            <Text size="sm" c="dimmed">
              {`${interestedCount} ${
                interestedCount === 1 ? 'agency is' : 'agencies are'
              } interested in you`}
            </Text>
          )}
        </Stack>
        <InterestedAgencies
          interestedAgencies={interested as any[]}
          loading={isLoading}
        />
      </Card>
    </Stack>
  );
}

export default MyAgent;
